import { useContext, useEffect, useRef, useState } from 'react';
import { ProgramContext } from '../../context/ProgramsContext';
import { AlertTriangle } from 'lucide-react';

const PatientSearchInput = ({ value, onChange, disabled, isDirty }) => {
    const { mockApi, headerData } = useContext(ProgramContext);
    const [searchTerm, setSearchTerm] = useState(value);
    const [results, setResults] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const wrapperRef = useRef(null);
    const dropdownRef = useRef(null);
    const [pendingAction, setPendingAction] = useState(null)
    const [showIsDirtyModal, setShowIsDirtyModal] = useState(false)

    useEffect(() => {
        setSearchTerm(value);
    }, [value]);

    useEffect(() => {
        const handler = setTimeout(async () => {
            const fetchedPatients = await mockApi.fetchPatients(searchTerm);
            if (searchTerm.length > 1) {
                setResults(fetchedPatients.filter(p => p?.value !== value));
            } else {
                setResults(fetchedPatients?.filter((e) => e?.value));
            }
            setHighlightedIndex(-1); // Reset highlight
        }, 300);
        return () => clearTimeout(handler);
    }, [searchTerm, value]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleKeyDown = (e) => {
        if (!showDropdown || results.length === 0) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightedIndex((prev) => (prev + 1) % results.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightedIndex((prev) => (prev - 1 + results.length) % results.length);
        } else if (e.key === 'Enter' && highlightedIndex >= 0) {
            e.preventDefault();
            if (isDirty) {
                setPendingAction(results[highlightedIndex]);
                setShowIsDirtyModal(true)
            } else {
                onChange(results[highlightedIndex]);
            }
            setSearchTerm(results[highlightedIndex].value);
            setShowDropdown(false);
        }
    };

    useEffect(() => {
        if (highlightedIndex >= 0 && dropdownRef.current) {
            const el = dropdownRef.current.children[highlightedIndex];
            if (el) el.scrollIntoView({ block: 'nearest' });
        }
    }, [highlightedIndex]);

    function onCancel() {
        setShowIsDirtyModal(false)
        setPendingAction(null)
        setSearchTerm(headerData?.patientName)
    }

    function onConfirm() {
        onChange(pendingAction)
        setShowIsDirtyModal(false)
    }

    return (
        <>
            <div className="relative" ref={wrapperRef}>
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onFocus={() => setShowDropdown(true)}
                    onKeyDown={handleKeyDown}
                    onBlur={() => setSearchTerm(headerData?.patientName)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    disabled={disabled}
                />

                {(showDropdown && !(headerData?.patientName && (headerData?.patientName === searchTerm) && (results?.length === 0))) && (
                    <div
                        className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-md mt-1 shadow-lg z-10 max-h-60 overflow-auto"
                        ref={dropdownRef}
                    >
                        {results.length > 0 ? (
                            results.map((p, index) => (
                                <div
                                    key={index}
                                    onClick={() => {
                                        if (isDirty) {
                                            setPendingAction(p);
                                            setShowIsDirtyModal(true)
                                        } else {
                                            onChange(p);
                                        }
                                        setSearchTerm(p.value);
                                        setShowDropdown(false);
                                    }}
                                    className={`px-4 py-2 cursor-pointer transition ${index === highlightedIndex ? 'bg-[#166273]/10' : 'text-gray-800 hover:bg-gray-100'
                                        }`}
                                >
                                    {p?.value}
                                </div>
                            ))
                        ) : (
                            <div className="px-4 py-2 text-gray-500 select-none cursor-default">
                                No Patient Found...
                            </div>
                        )}
                    </div>
                )}

            </div>

            {showIsDirtyModal &&
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50 animate-fade-in-up">
                    <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 max-w-[90vw] sm:max-w-md w-full">
                        <div className="text-center">
                            <AlertTriangle className="mx-auto h-12 w-12 text-yellow-500" />
                            <h3 className="mt-2 text-xl font-bold text-gray-800">Unsaved Changes</h3>
                            <p className="mt-2 text-sm text-gray-600">
                                You have unsaved changes. Are you sure you want to discard them?
                            </p>
                        </div>

                        <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
                            <button
                                onClick={onCancel}
                                className="px-4 py-2 text-gray-700 font-semibold rounded-lg border border-gray-300 hover:bg-gray-100 transition"
                            >
                                Stay
                            </button>
                            <button
                                onClick={onConfirm}
                                className="px-4 py-2 bg-red-600 text-white font-semibold rounded-lg shadow-md hover:bg-red-700 transition"
                            >
                                Discard
                            </button>
                        </div>
                    </div>
                </div>
            }
        </>
    );
};

export default PatientSearchInput;
